import {
  SsoMembership,
  SsoStatus,
  SsoType,
} from '@tribeplatform/gql-client/types'
import { useDomain } from '@tribeplatform/react-components/common/hooks'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import {
  useLoginWithSso,
  useSsoMemberships,
  useSsos,
  useDeleteSsoMembership,
} from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import { SvgSocialIcon } from '@tribeplatform/react-ui-kit/Icon'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { getSsoCallbackUrl } from '../../../server/middlewares/ssos/utils/sso.utils.js'
import { SsoIcon } from '../Auth/components/SsoIcons.js'

const isMember = (ssoType: SsoType, memberships: SsoMembership[]) => {
  return memberships?.find(membership => membership.ssoType === ssoType)
}

export const ConnectedAccounts = ({ member }: { member: { id: string } }) => {
  const { $t } = useI18n()
  const { getResourceUrl } = useDomain()
  const { data: ssos } = useSsos({
    variables: { status: SsoStatus.enable },
  })
  const { login } = useLoginWithSso()
  const { mutate: deleteMembership } = useDeleteSsoMembership()
  const { data: memberships, refetch: refetchMemberships } = useSsoMemberships({
    variables: {
      memberId: member.id,
    },
  })
  const defaultSsos = ssos?.filter(
    sso => sso.type !== SsoType.oauth2 && sso.type !== SsoType.jwt,
  )

  const toggleSso = async (type: SsoType) => {
    const isConnected = isMember(type, memberships)

    if (isConnected) {
      deleteMembership(
        {
          memberId: member.id,
          type,
        },
        {
          onSuccess: () => {
            toast({
              title: $t({
                defaultMessage: 'Successfully disconnected the account.',
                id: 'Settings.ConnectedAccounts.Disconnected',
              }),
              status: 'success',
            })
          },
          onError: () => {
            toast({
              title: $t({
                defaultMessage: 'Unable to disconnect the account.',
                id: 'Settings.ConnectedAccounts.UnableToDisconnect',
              }),
              status: 'error',
            })
          },
          onSettled: () => {
            refetchMemberships()
          },
        },
      )
    } else {
      const callbackUrl = getSsoCallbackUrl(
        type,
        getResourceUrl,
        '/settings/login',
      )

      const response = await login({
        input: {
          type,
          callbackUrl,
        },
      })

      window.location.href = response.url
    }
  }

  if (!defaultSsos || !defaultSsos.length) {
    return null
  }

  return (
    <Card>
      <Card.Header
        title={$t({
          defaultMessage: 'Connected Accounts',
          id: 'Settings.ConnectedAccounts',
        })}
      />
      <Card.Content>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {defaultSsos.map(({ type, name, buttonText }) => (
            <Button
              href="#"
              onClick={() => toggleSso(type)}
              className="justify-center"
              key={type}
              variant="secondaryNeutral"
              size="lg"
              leadingIcon={
                SsoIcon[type] && (
                  <SvgSocialIcon
                    size="xl"
                    name={SsoIcon[type]}
                    rounded
                    className="me-0"
                  />
                )
              }
            >
              {isMember(type, memberships) ? (
                <T
                  defaultMessage="Disconnect"
                  id="Settings.ConnectedAccounts.Disconnect"
                />
              ) : (
                <T
                  defaultMessage="Connect with {provider_name}"
                  id="Settings.ConnectedAccounts.Connect"
                  values={{
                    provider_name: buttonText || name,
                  }}
                />
              )}
            </Button>
          ))}
        </div>
      </Card.Content>
    </Card>
  )
}
